import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook to check user rights for specified module, menu and optional submenu.
 * Handles auto-bypass for super_admin and admin roles.
 */
export function useRights(module, menu, submenu = null) {
  const { user, rights: globalRights } = useAuth();
  
  // Use rights from auth state (synced from redux/localStorage)
  const rights = useMemo(() => globalRights || {}, [globalRights]);
  const role = user?.role;

  return useMemo(() => {
    // 1. Super Admin Bypass
    if (role === 'super_admin') {
      return {
        canView: true,
        canViewAll: true,
        canViewSelf: true,
        canAdd: true,
        canAddAll: true,
        canAddSelf: true,
        canApply: true,
        canApplyAll: true,
        canApplySelf: true,
        canEdit: true,
        canEditAll: true,
        canEditSelf: true,
        canDelete: true,
        canDeleteAll: true,
        canDeleteSelf: true,
        canExport: true,
        canImport: true,
        canApprove: true,
        canApproveSelf: true,
        canApproveAll: true,
        canDuplicate: true,
        isAdmin: true,
        hasRight: () => true,
      };
    }

    // 2. Admin Bypass (Everything except super_admin module)
    if (role === 'admin') {
      const isSuperAdminModule = module === 'super_admin';
      const hasAll = !isSuperAdminModule;
      return {
        canView: hasAll,
        canViewAll: hasAll,
        canViewSelf: hasAll,
        canAdd: hasAll,
        canAddAll: hasAll,
        canAddSelf: hasAll,
        canApply: hasAll,
        canApplyAll: hasAll,
        canApplySelf: hasAll,
        canEdit: hasAll,
        canEditAll: hasAll,
        canEditSelf: hasAll,
        canDelete: hasAll,
        canDeleteAll: hasAll,
        canDeleteSelf: hasAll,
        canExport: hasAll,
        canImport: hasAll,
        canApprove: hasAll,
        canApproveSelf: hasAll,
        canApproveAll: hasAll,
        canDuplicate: hasAll,
        isAdmin: hasAll,
        hasRight: () => hasAll,
      };
    }

    // 3. Moderator Check (Rights-based)
    // If no module is provided, return a permissive default so we don't break existing views that haven't opted-in yet
    if (!module || !menu) {
      return {
        canView: true, canViewAll: true, canViewSelf: true,
        canAdd: true, canAddAll: true, canAddSelf: true,
        canApply: true, canApplyAll: true, canApplySelf: true,
        canEdit: true, canEditAll: true, canEditSelf: true,
        canDelete: true, canDeleteAll: true, canDeleteSelf: true,
        canExport: true, canImport: true, canApprove: true, canApproveSelf: true, canApproveAll: true,
        canDuplicate: true, isAdmin: false,
        hasRight: () => true,
      };
    }

    const moduleItem = rights[module];
    if (!moduleItem) {
      // Opted-in but module missing in rights -> access denied
      return getDeniedObject();
    }

    // Map legacy/incorrect 'user_management' to 'user_staff_management' for submenus that live under 'user_staff_management'
    let effectiveMenu = menu;
    if (module === 'hrm_management' && menu === 'user_management') {
      const staffSubmenus = [
        'users',
        'designation',
        'user_groups',
        'user_rights',
        'geography_rights',
        'reporting_hierarchy',
        'staff_migrations',
        'holiday'
      ];
      if (submenu && staffSubmenus.includes(submenu)) {
        effectiveMenu = 'user_staff_management';
      }
    }

    const menuItems = moduleItem.children || {};
    const menuItem = menuItems[effectiveMenu];
    if (!menuItem) {
       // Opted-in but menu missing in rights -> access denied
      return getDeniedObject();
    }

    let target = menuItem;
    if (submenu) {
      const submenuItems = menuItem.children || {};
      target = submenuItems[submenu] || {};
    }

    const actions = target || {};

    // Helper to check standard action or combined
    const check = (act) => !!actions[act];

    return {
      canView: check('view_all') || check('view_self'),
      canViewAll: check('view_all'),
      canViewSelf: check('view_self'),
      
      canAdd: check('add') || check('add_all') || check('add_self') || check('apply_all') || check('apply_self') || check('assign_team'),
      canAddAll: check('add') || check('add_all') || check('apply_all'),
      canAddSelf: check('add_self') || check('apply_self'),
      
      canApply: check('apply_all') || check('apply_self') || check('add_all') || check('add_self') || check('add'),
      canApplyAll: check('apply_all') || check('add_all') || check('add'),
      canApplySelf: check('apply_self') || check('add_self'),
      
      canEdit: check('edit_all') || check('edit_self') || check('upload_documents') || check('change_password'),
      canEditAll: check('edit_all'),
      canEditSelf: check('edit_self'),
      
      canDelete: check('delete_all') || check('delete_self'),
      canDeleteAll: check('delete_all'),
      canDeleteSelf: check('delete_self'),
      
      canExport: check('export'),
      canImport: check('import'),
      
      canApprove: check('approve') || check('approve_self') || check('review') || check('reject'),
      canApproveAll: check('approve'),
      canApproveSelf: check('approve_self'),
      canDuplicate: check('duplicate'),
      
      isAdmin: false,
      hasRight: (act) => check(act),
    };
  }, [module, menu, submenu, rights, role]);
}

function getDeniedObject() {
  return {
    canView: false, canViewAll: false, canViewSelf: false,
    canAdd: false, canAddAll: false, canAddSelf: false,
    canApply: false, canApplyAll: false, canApplySelf: false,
    canEdit: false, canEditAll: false, canEditSelf: false,
    canDelete: false, canDeleteAll: false, canDeleteSelf: false,
    canExport: false, canImport: false, canApprove: false, canApproveSelf: false, canApproveAll: false,
    canDuplicate: false, isAdmin: false,
    hasRight: () => false,
  };
}
